import { Route, Switch } from 'react-router-dom'
import loadable from '@loadable/component'

import SearchLoadingView from '../components/layout/ItemGridWithMap/LoadingView'

// A leaf module (not the home barrel) so importing it doesn't defeat the
// lazy Home split below.
import { countryPathConstraints, marketPathConstraints } from './marketPaths'

// Auth pages share one chunk. webpackPrefetch hints the browser to fetch
// after the main page is idle — users hitting an auth gate is a common
// next-action on public browse pages.
const Login = loadable(
  () =>
    import(
      /* webpackChunkName: "auth", webpackPrefetch: true */ '../components/auth/Login'
    ),
)
const Signup = loadable(
  () =>
    import(
      /* webpackChunkName: "auth", webpackPrefetch: true */ '../components/auth/Signup'
    ),
)
const ResetPassword = loadable(
  () =>
    import(/* webpackChunkName: "auth" */ '../components/auth/ResetPassword'),
)
const ResendConfirmation = loadable(
  () =>
    import(
      /* webpackChunkName: "auth" */ '../components/auth/ResendConfirmation'
    ),
)
const NewPassword = loadable(
  () => import(/* webpackChunkName: "auth" */ '../components/auth/NewPassword'),
)
const SetPassword = loadable(
  () => import(/* webpackChunkName: "auth" */ '../components/auth/SetPassword'),
)
const ConfirmEmail = loadable(
  () =>
    import(/* webpackChunkName: "auth" */ '../components/auth/ConfirmEmail'),
)

const Onboarding = loadable(
  () =>
    import(
      /* webpackChunkName: "onboarding" */ '../pages/hostOnboarding/onboarding'
    ),
)
const OnboardingUsa = loadable(
  () =>
    import(
      /* webpackChunkName: "onboarding" */ '../pages/hostOnboarding/onboardingUsa'
    ),
)
const StorytellerOnboarding = loadable(
  () =>
    import(
      /* webpackChunkName: "onboarding" */ '../pages/storytellerOnboarding'
    ),
)

const AboutStoriesHost = loadable(
  () =>
    import(
      /* webpackChunkName: "static-marketing" */ '../pages/aboutStories/Host'
    ),
)
const AboutStoriesCustomer = loadable(
  () =>
    import(
      /* webpackChunkName: "static-marketing" */ '../pages/aboutStories/Customer'
    ),
)
const AboutStoriesStoryteller = loadable(
  () =>
    import(
      /* webpackChunkName: "static-marketing" */ '../pages/aboutStories/Storyteller'
    ),
)
const Contact = loadable(
  () =>
    import(
      /* webpackChunkName: "static-marketing" */ '../pages/static/Contact'
    ),
)
const HowItWorks = loadable(
  () =>
    import(
      /* webpackChunkName: "static-marketing" */ '../pages/static/HowItWorks'
    ),
)
const About = loadable(
  () =>
    import(/* webpackChunkName: "static-marketing" */ '../pages/static/About'),
)
const Team = loadable(
  () =>
    import(/* webpackChunkName: "static-marketing" */ '../pages/static/Team'),
)
const DownloadApp = loadable(
  () =>
    import(/* webpackChunkName: "static-marketing" */ '../pages/downloadApp'),
)

const VoucherRedeem = loadable(
  () =>
    import(
      /* webpackChunkName: "vouchers-public" */ '../pages/vouchers/Redeem'
    ),
)
const VoucherLanding = loadable(
  () => import(/* webpackChunkName: "vouchers-public" */ '../pages/vouchers'),
)
const XMasVouchers = loadable(
  () =>
    import(
      /* webpackChunkName: "vouchers-public" */ '../readymade/pages/XmasVouchers'
    ),
)

const ExploreRegion = loadable(
  () => import(/* webpackChunkName: "region" */ '../pages/static/Region'),
)

const Place = loadable(
  () => import(/* webpackChunkName: "place" */ '../pages/static/Place'),
)

const Collection = loadable(
  () =>
    import(
      /* webpackChunkName: "collection" */ '../pages/collections/Collection'
    ),
)

const CollectionsIndex = loadable(
  () =>
    import(
      /* webpackChunkName: "collection" */ '../pages/collections/CollectionsIndex'
    ),
)

const Home = loadable(
  () => import(/* webpackChunkName: "home" */ '../pages/home'),
)

// Listing, Story, and Adventure detail pages share gallery, share/rail,
// review, and date-picker code — co-locating them in one chunk avoids
// re-downloading shared app code as the user navigates between them.
// webpackPrefetch hints the browser to fetch when likely-next (from
// home or search).
const Listing = loadable(
  () =>
    import(
      /* webpackChunkName: "detail", webpackPrefetch: true */ '../pages/listing'
    ),
)
const Story = loadable(
  () =>
    import(
      /* webpackChunkName: "detail", webpackPrefetch: true */ '../pages/story'
    ),
)
const Adventure = loadable(
  () =>
    import(
      /* webpackChunkName: "detail", webpackPrefetch: true */ '../pages/adventure/AdventureDetailsPage'
    ),
)
const UserProfile = loadable(
  () =>
    import(/* webpackChunkName: "user-profile" */ '../pages/user/UserProfile'),
)

// No webpackPrefetch hint — search is rarely the next click from home,
// the chunk is small (~18 KB gz), and the wasted bytes on home outweigh
// the latency saving for the minority who do click "Search". Prefetch
// stays on `detail` above because click-through from a card is the
// dominant next action from the home page.
const SearchResults = loadable(
  () => import(/* webpackChunkName: "search" */ '../search'),
  { fallback: <SearchLoadingView /> },
)

export default (
  <Switch>
    <Route exact path="/login" component={Login} />
    <Route exact path="/signup" component={Signup} />
    <Route exact path="/set-password" component={SetPassword} />
    <Route exact path="/reset-password" component={ResetPassword} />
    <Route exact path="/new-password" component={NewPassword} />
    <Route exact path="/resend-confirmation" component={ResendConfirmation} />
    <Route exact path="/confirm-email" component={ConfirmEmail} />
    <Route
      exact
      path="/:country([a-z]{2})/:market?/collections"
      component={CollectionsIndex}
    />
    <Route
      exact
      path="/:country([a-z]{2})/:market?/collections/:slug"
      component={Collection}
    />
    <Route
      exact
      path={`/:country${countryPathConstraints}/:market${marketPathConstraints}/:regionId/:placeId`}
      component={Place}
    />
    <Route exact path="/:market(nz)/:regionId/:placeId" component={Place} />
    <Route
      exact
      path={`/:country${countryPathConstraints}/:market${marketPathConstraints}/:regionId`}
      component={ExploreRegion}
    />
    {/* Collapsed form for markets named after their country */}
    <Route exact path="/:market(nz)/:regionId" component={ExploreRegion} />
    <Route path="/about" component={About} />
    <Route path="/team" component={Team} />
    <Route path="/stories-hosts" component={AboutStoriesHost} />
    <Route path="/stories-travellers" component={AboutStoriesCustomer} />
    <Route path="/stories-storytellers" component={AboutStoriesStoryteller} />
    <Route exact path="/vouchers/christmas" component={XMasVouchers} />
    <Route path="/onboarding" component={Onboarding} />
    <Route path="/storyteller-onboarding" component={StorytellerOnboarding} />
    <Route path="/list-with-us" component={Onboarding} />
    <Route path="/hosting/usa" component={OnboardingUsa} />
    <Route path="/how-it-works" component={HowItWorks} />
    <Route path="/contact-us" component={Contact} />
    <Route path="/download" component={DownloadApp} />
    <Route exact path="/redeem" component={VoucherRedeem} />
    <Route exact path="/vouchers" component={VoucherLanding} />
    <Route path="/adventures/:id" component={Adventure} />
    <Route path="/stories/:id" component={Story} />
    <Route path="/users/:userId" component={UserProfile} />
    <Route path="/listings/:id" component={Listing} />
    <Route exact path="/adventures" component={SearchResults} />
    <Route exact path="/stories" component={SearchResults} />
    <Route exact path="/listings" component={SearchResults} />
    <Route exact path={`/:market${marketPathConstraints}`} component={Home} />
    <Route exact path={`/:country${countryPathConstraints}`} component={Home} />
    <Route
      exact
      path={`/:country${countryPathConstraints}/:market${marketPathConstraints}`}
      component={Home}
    />
    <Route exact path="/" component={Home} />
  </Switch>
)
